import React, { useState, useRef, useEffect } from "react";
import SyntaxHighlighter from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import Markdown from "markdown-to-jsx";
import { FaCopy, FaChevronRight, FaPaperclip, FaFilePdf, FaFileImage, FaTimes } from "react-icons/fa";
import Header from './Header';
import Sidebar from './Sidebar';
import "./Chat.css";

const GET_UPLOAD_URL_API = "https://ieljd2xnc6.execute-api.us-west-2.amazonaws.com/get-presigned-url";

const Chat = ({ onLogout, token }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);

  // State now holds S3 file references { key, fileName, fileType }
  const [activeFileContexts, setActiveFileContexts] = useState([]);

  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const savedChats = JSON.parse(localStorage.getItem('chats')) || [];
    setChats(savedChats);
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [input]);

  const saveChatsToStorage = (updatedChats) => {
    localStorage.setItem('chats', JSON.stringify(updatedChats));
    setChats(updatedChats);
  };

  // NEW: Rewritten file upload logic for S3
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    let chatId = currentChatId;
    if (!chatId) {
      const newChat = { id: Date.now(), title: file.name, messages: [], fileContexts: [] };
      setChats(prev => [...prev, newChat]);
      chatId = newChat.id;
      setCurrentChatId(chatId);
    }

    setLoading(true); // Show a loading spinner during upload

    try {
      // 1. Get the secure, pre-signed URL from our new Lambda function
      const presignResponse = await fetch(GET_UPLOAD_URL_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const { uploadURL, key } = await presignResponse.json();

      // 2. Upload the file directly to S3 using the pre-signed URL
      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      if (!uploadResponse.ok) {
        throw new Error('S3 upload failed.');
      }

      // 3. Success! Store the S3 key reference, not the file itself.
      const fileContextReference = { key, fileName: file.name, fileType: file.type };
      const updatedContexts = [...activeFileContexts, fileContextReference];
      setActiveFileContexts(updatedContexts);

      const fileMessage = { type: 'file', fileName: file.name, fileType: file.type };
      setMessages(prev => [...prev, fileMessage]);

      const chatToUpdate = currentChatId || chatId;
      const updatedChats = chats.map(chat =>
        chat.id === chatToUpdate
          ? { ...chat, messages: [...(chat.messages || []), fileMessage], fileContexts: updatedContexts }
          : chat
      );

      if (!updatedChats.find(c => c.id === chatToUpdate)) {
        const newChat = { id: chatId, title: file.name, messages: [fileMessage], fileContexts: updatedContexts };
        saveChatsToStorage([...chats, newChat]);
      } else {
        saveChatsToStorage(updatedChats);
      }

    } catch (error) {
      console.error("Error uploading file:", error);
      // You could add a user-facing error message here
    } finally {
      setLoading(false);
    }
  };

  // MODIFIED: Sends S3 keys instead of file data
  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { sender: "user", text: input };
    setMessages(prev => [...prev, userMessage]);

    let chatId = currentChatId;
    let updatedChats;

    if (!chatId) {
      chatId = Date.now();
      setCurrentChatId(chatId);
      const newChat = { id: chatId, title: input.slice(0, 30), messages: [userMessage], fileContexts: activeFileContexts };
      updatedChats = [...chats, newChat];
    } else {
      updatedChats = chats.map(chat =>
        chat.id === chatId ? { ...chat, messages: [...chat.messages, userMessage] } : chat
      );
    }

    saveChatsToStorage(updatedChats);
    setInput("");
    setLoading(true);

    try {
      const payload = {
        prompt: input,
        context,
      };

      if (activeFileContexts.length > 0) {
        // Send an array of S3 keys
        payload.file_keys = activeFileContexts.map(f => f.key);
      }

      const response = await fetch("https://2mo6y6fst1.execute-api.us-west-2.amazonaws.com/prod/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      const botMessage = { sender: "bot", text: data.reply };
      setMessages(prev => [...prev, botMessage]);
      setContext(data.context);

      const finalChats = updatedChats.map(chat =>
        chat.id === chatId ? { ...chat, messages: [...chat.messages, botMessage] } : chat
      );
      saveChatsToStorage(finalChats);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChat = (chatId) => {
    const selectedChat = chats.find(chat => chat.id === chatId);
    if (selectedChat) {
      setMessages(selectedChat.messages);
      setActiveFileContexts(selectedChat.fileContexts || []);
      setContext("");
      setCurrentChatId(chatId);
      setIsSidebarOpen(false);
    }
  };

  const handleDeleteChat = (chatId) => {
    // Note: This only deletes the *reference* from localStorage.
    // The actual S3 object remains. Automatic deletion requires an S3 lifecycle policy.
    const updatedChats = chats.filter(chat => chat.id !== chatId);
    saveChatsToStorage(updatedChats);
    if (currentChatId === chatId) {
      setMessages([]);
      setCurrentChatId(null);
      setActiveFileContexts([]);
    }
  };

  const handleDeleteFile = (fileIndexToDelete) => {
    const fileToDelete = activeFileContexts[fileIndexToDelete];
    // Note: This only deletes the reference. A separate Lambda would be needed to delete the S3 object.
    console.log(`Requesting deletion for S3 key: ${fileToDelete.key}`);

    const updatedContexts = activeFileContexts.filter((_, index) => index !== fileIndexToDelete);
    setActiveFileContexts(updatedContexts);

    if (currentChatId) {
      const updatedChats = chats.map(chat =>
        chat.id === currentChatId ? { ...chat, fileContexts: updatedContexts } : chat
      );
      saveChatsToStorage(updatedChats);
    }
  };

  const handleCopy = (text) => navigator.clipboard.writeText(text).then(() => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  });

  return (
    <div className="chat-container">
      {!isSidebarOpen && <button className="toggle-sidebar" onClick={() => setIsSidebarOpen(true)}><FaChevronRight /></button>}
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(false)} chats={chats} onSelectChat={handleSelectChat} onDeleteChat={handleDeleteChat} currentChatId={currentChatId} />
      <Header onLogout={onLogout} />
      {showToast && <div className="toast">Response copied</div>}
      <div className="chat-messages">
        {messages.map((message, index) => (
          <div key={index} className={`chat-message ${message.sender || message.type}`}>
            {message.type === 'file' ? (
              <div className="file-info">
                {message.fileType.includes('image') ? <FaFileImage className="file-icon" /> : <FaFilePdf className="file-icon" />}
                <span className="file-name">{message.fileName}</span>
              </div>
            ) : message.sender === 'bot' ? (
              <div>
                <Markdown options={{ overrides: { table: { component: ({ children, ...props }) => <div className="table-container"><table {...props}>{children}</table></div> }, code: { component: ({ className, children }) => <SyntaxHighlighter language={className?.replace('lang-', '') || 'text'} style={dracula}>{children}</SyntaxHighlighter> } } }}>{message.text}</Markdown>
                <FaCopy className="copy-icon" onClick={() => handleCopy(message.text)} />
              </div>
            ) : <div className="message-text">{message.text}</div>}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <div className="active-files-container">
          {activeFileContexts.map((file, index) => (
            <div key={index} className="active-file-tag">
              {file.fileType.includes('image') ? <FaFileImage /> : <FaFilePdf />}
              <span>{file.fileName}</span>
              <button onClick={() => handleDeleteFile(index)} className="delete-file-button">
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
        <div className="input-row">
          <div className="file-upload">
            <label htmlFor="file-input" className="upload-icon"><FaPaperclip /></label>
            <input id="file-input" type="file" onChange={handleFileUpload} accept="image/*, application/pdf" style={{ display: 'none' }} onClick={(e) => e.target.value = null} />
          </div>
          <textarea rows="1" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Type your message..." className="chat-textarea" ref={textareaRef} />
          <button onClick={handleSend} disabled={loading || !input.trim()} className="send-button">{loading ? <div className="spinner"></div> : 'Send'}</button>
        </div>
      </div>
    </div>
  );
};

export default Chat;